import * as React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { User } from 'oidc-client-ts';
import { useRuntimeConfig } from '@/config/ConfigProvider';
import { Button, Spinner } from '@/components/ui/button';
import { ErrorNotice } from '@/components/ui/misc';
import { bearerToken, completeSignIn, currentUser, signIn, signOut, userManager } from './oidc';

/**
 * Authentication state.
 *
 * There is no public surface in this application: no landing page, no registration,
 * no unauthenticated dashboard (the one exception is the public pay page, which lives
 * outside the auth guard entirely and makes only anonymous API calls).
 *
 * Authorization Code + PKCE against the shared Cognito pool: an unauthenticated visit to any
 * guarded route starts the SSO redirect immediately; the bearer is the Cognito **ID token**.
 */

export interface AuthProfile {
  readonly userId: string;
  readonly name?: string;
  readonly email?: string;
}

export interface AuthState {
  readonly status: 'loading' | 'authenticated' | 'anonymous';
  /** The bearer value for the next API request, refreshed if it is close to expiring. */
  readonly getToken: () => Promise<string | undefined>;
  readonly signOut: () => Promise<void>;
  /** The caller's own profile, for display. Never used for access decisions. */
  readonly profile: AuthProfile | undefined;
}

const AuthContext = React.createContext<AuthState | undefined>(undefined);

export function useAuth(): AuthState {
  const value = React.useContext(AuthContext);
  if (value === undefined) throw new Error('useAuth must be used within <AuthProvider>.');
  return value;
}

// ---------------------------------------------------------------------------- oidc mode

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const config = useRuntimeConfig();
  const [user, setUser] = React.useState<User | undefined>(undefined);
  const [status, setStatus] = React.useState<AuthState['status']>('loading');

  React.useEffect(() => {
    const oidc = userManager(config);
    let cancelled = false;

    currentUser(config).then(
      (existing) => {
        if (cancelled) return;
        setUser(existing);
        setStatus(existing ? 'authenticated' : 'anonymous');
      },
      () => {
        if (!cancelled) setStatus('anonymous');
      },
    );

    // Session expiry and silent renewal both change the session out from under the
    // UI; without these the header would keep showing a signed-in user whose token
    // no longer works, and every API call would start failing with no explanation.
    const onLoaded = (next: User) => {
      setUser(next);
      setStatus('authenticated');
    };
    const onGone = () => {
      setUser(undefined);
      setStatus('anonymous');
    };
    const onRenewError = (error: unknown) => {
      console.warn('Silent token renewal failed; the session has ended.', error);
      setUser(undefined);
      setStatus('anonymous');
    };

    oidc.events.addUserLoaded(onLoaded);
    oidc.events.addUserUnloaded(onGone);
    oidc.events.addAccessTokenExpired(onGone);
    oidc.events.addSilentRenewError(onRenewError);

    return () => {
      cancelled = true;
      oidc.events.removeUserLoaded(onLoaded);
      oidc.events.removeUserUnloaded(onGone);
      oidc.events.removeAccessTokenExpired(onGone);
      oidc.events.removeSilentRenewError(onRenewError);
    };
  }, [config]);

  const getToken = React.useCallback(async () => {
    // Read through the manager rather than from React state: a silent renewal may
    // have replaced the token microseconds ago, and a stale closure would send the
    // old one and get a 401 for no reason.
    const fresh = await currentUser(config);
    if (!fresh) return undefined;
    return bearerToken(fresh);
  }, [config]);

  const value = React.useMemo<AuthState>(() => {
    const profile: AuthProfile | undefined = user
      ? {
          userId: String(user.profile.sub),
          ...(typeof user.profile.name === 'string' ? { name: user.profile.name } : {}),
          ...(typeof user.profile.email === 'string' ? { email: user.profile.email } : {}),
        }
      : undefined;

    return {
      status,
      getToken,
      signOut: () => signOut(config),
      profile,
    };
  }, [user, status, getToken, config]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ----------------------------------------------------------------------------- guards

/**
 * Gates a route behind authentication.
 *
 * Once authenticated this renders its children
 * straight away. In oidc mode an anonymous visitor is redirected to the identity
 * provider, carrying where they were going so they arrive there afterwards. Nothing
 * protected renders first.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const config = useRuntimeConfig();
  const { status } = useAuth();
  const location = useLocation();
  const [redirecting, setRedirecting] = React.useState(false);

  React.useEffect(() => {
    if (config.auth.mode !== 'oidc') return;
    if (status !== 'anonymous' || redirecting) return;
    setRedirecting(true);
    const returnTo = `${location.pathname}${location.search}`;
    signIn(config, returnTo).catch((error) => {
      console.error('Could not start the sign-in redirect', error);
      setRedirecting(false);
    });
  }, [config, status, redirecting, location.pathname, location.search]);

  if (status === 'authenticated') return <>{children}</>;

  return (
    <div className="flex h-full items-center justify-center" role="status">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        {status === 'loading' ? 'Checking your session…' : 'Redirecting to sign in…'}
      </div>
    </div>
  );
}

/**
 * The OAuth redirect target.
 *
 * Exchanges the authorization code, then replaces the history entry so the code and
 * state never stay in the address bar or in a copied link. Reached only in oidc mode.
 */
export function AuthCallback() {
  const config = useRuntimeConfig();
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | undefined>(undefined);
  const handled = React.useRef(false);

  React.useEffect(() => {
    if (config.auth.mode !== 'oidc') {
      navigate('/', { replace: true });
      return;
    }
    // Strict mode runs effects twice in development, and exchanging a one-time
    // authorization code twice fails the second time. The guard keeps that from
    // looking like a real sign-in failure.
    if (handled.current) return;
    handled.current = true;

    completeSignIn(config).then(
      ({ returnTo }) => navigate(returnTo, { replace: true }),
      (cause) => {
        console.error('Sign-in could not be completed', cause);
        setError(
          'Sign-in could not be completed. This usually means the attempt expired or was ' +
            'opened in a different browser tab. Try signing in again.',
        );
      },
    );
  }, [config, navigate]);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="w-full max-w-md space-y-3">
          <ErrorNotice title="Sign-in failed" message={error} />
          <Button onClick={() => navigate('/', { replace: true })}>Start again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center" role="status">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        Completing sign-in…
      </div>
    </div>
  );
}

/** Sends an already-authenticated visitor at the callback path to the dashboard. */
export function RedirectIfAuthenticated({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  if (status === 'authenticated') return <Navigate to="/" replace />;
  return <>{children}</>;
}
